import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { tournamentsAPI, venuesAPI } from '../lib/api';
import VenueCombobox from '../components/VenueCombobox';
import { formatDate, cn } from '../lib/utils';
import { Trophy, Plus, Users, MapPin, CalendarIcon, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

export default function Tournaments() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tournaments, setTournaments] = useState([]);
  const [venues, setVenues] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newTournament, setNewTournament] = useState({
    name: '',
    format: 'knockout',
    match_type: 'singles',
    max_participants: 16,
    venue_id: '',
    start_date: null,
    end_date: null,
    organizer_name: '',
    organizer_phone: '',
    points_per_game: 21,
    best_of: 1,
    shuttle_type: 'feather',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Tournaments list is the required call. Venues lookup is best-effort
        // so an anonymous viewer (or one with a transient venues 5xx) still
        // sees the tournaments list and isn't shown a blank screen.
        const tournamentsRes = await tournamentsAPI.getAll();
        setTournaments(tournamentsRes.data || []);
        try {
          const venuesRes = await venuesAPI.getAll();
          setVenues(venuesRes.data || []);
        } catch { /* leave venues empty — the venue dropdown stays empty */ }
      } catch (error) {
        console.error('Error fetching tournaments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCreateTournament = async () => {
    if (!newTournament.name.trim()) {
      toast.error('Tournament name is required');
      return;
    }

    setCreating(true);
    try {
      const response = await tournamentsAPI.create({
        name: newTournament.name,
        format: newTournament.format,
        match_type: newTournament.match_type,
        max_participants: parseInt(newTournament.max_participants),
        venue_id: newTournament.venue_id && newTournament.venue_id !== 'none' ? newTournament.venue_id : null,
        start_date: newTournament.start_date,
        end_date: newTournament.end_date,
        organizer_name: newTournament.organizer_name || null,
        organizer_phone: newTournament.organizer_phone || null,
        points_per_game: parseInt(newTournament.points_per_game),
        best_of: parseInt(newTournament.best_of),
        shuttle_type: newTournament.shuttle_type,
      });
      
      setTournaments([response.data, ...tournaments]);
      setCreateDialogOpen(false);
      setNewTournament({
        name: '',
        format: 'knockout',
        match_type: 'singles',
        max_participants: 16,
        venue_id: '',
        start_date: null,
        end_date: null,
        organizer_name: '',
        organizer_phone: '',
        points_per_game: 21,
        best_of: 1,
        shuttle_type: 'feather',
      });
      toast.success('Tournament created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tournament');
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      registration: 'bg-blue-500',
      in_progress: 'bg-green-500',
      completed: 'bg-gray-500',
    };
    return styles[status] || 'bg-gray-500';
  };

  const getFormatLabel = (format) => {
    const labels = {
      knockout: 'Knockout',
      double_elimination: 'Double Elimination',
      round_robin: 'Round Robin',
      group_playoff: 'Group + Playoff',
    };
    return labels[format] || format;
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="tournaments-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Tournaments</h1>
            <p className="text-muted-foreground mt-1">
              {tournaments.length} tournament{tournaments.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-secondary hover:bg-secondary/90" data-testid="create-tournament-btn">
                <Plus className="mr-2 h-4 w-4" />
                Create Tournament
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Tournament</DialogTitle>
                <DialogDescription>
                  Set up a new badminton tournament
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Tournament Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Spring Championship 2026"
                    value={newTournament.name}
                    onChange={(e) => setNewTournament({ ...newTournament, name: e.target.value })}
                    data-testid="tournament-name-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Format</Label>
                    <Select
                      value={newTournament.format}
                      onValueChange={(value) => setNewTournament({ ...newTournament, format: value })}
                    >
                      <SelectTrigger data-testid="tournament-format-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="knockout">Knockout</SelectItem>
                        <SelectItem value="double_elimination">Double Elimination</SelectItem>
                        <SelectItem value="round_robin">Round Robin</SelectItem>
                        <SelectItem value="group_playoff">Group + Playoff</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Match Type</Label>
                    <Select
                      value={newTournament.match_type}
                      onValueChange={(value) => setNewTournament({ ...newTournament, match_type: value })}
                    >
                      <SelectTrigger data-testid="tournament-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="singles">Singles</SelectItem>
                        <SelectItem value="doubles">Doubles</SelectItem>
                        <SelectItem value="both">Both — Singles + Doubles</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Max Participants</Label>
                  <Select
                    value={newTournament.max_participants.toString()}
                    onValueChange={(value) => setNewTournament({ ...newTournament, max_participants: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="tournament-participants-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="4">4 Players</SelectItem>
                      <SelectItem value="8">8 Players</SelectItem>
                      <SelectItem value="16">16 Players</SelectItem>
                      <SelectItem value="32">32 Players</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Venue</Label>
                  <VenueCombobox
                    venues={venues}
                    value={newTournament.venue_id}
                    onChange={(value) => setNewTournament({ ...newTournament, venue_id: value })}
                    onVenueCreated={(v) => setVenues(prev => [v, ...prev])}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Organizer Name</Label>
                    <Input
                      placeholder="Your name"
                      value={newTournament.organizer_name}
                      onChange={(e) => setNewTournament({ ...newTournament, organizer_name: e.target.value })}
                      data-testid="organizer-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      placeholder="Contact number"
                      value={newTournament.organizer_phone}
                      onChange={(e) => setNewTournament({ ...newTournament, organizer_phone: e.target.value })}
                      data-testid="organizer-phone-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Points/Game</Label>
                    <Select
                      value={newTournament.points_per_game.toString()}
                      onValueChange={(value) => setNewTournament({ ...newTournament, points_per_game: parseInt(value) })}
                    >
                      <SelectTrigger data-testid="points-per-game-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 Points</SelectItem>
                        <SelectItem value="21">21 Points</SelectItem>
                        <SelectItem value="30">30 Points</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Best of</Label>
                    <Select
                      value={newTournament.best_of.toString()}
                      onValueChange={(value) => setNewTournament({ ...newTournament, best_of: parseInt(value) })}
                    >
                      <SelectTrigger data-testid="best-of-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 Game</SelectItem>
                        <SelectItem value="3">3 Games</SelectItem>
                        <SelectItem value="5">5 Games</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    Match Rules — Shuttle <span className="text-[10px] text-muted-foreground font-normal">(used by umpires during play)</span>
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setNewTournament({ ...newTournament, shuttle_type: 'nylon' })}
                      className={`h-10 rounded-md border-2 text-sm font-medium transition-colors ${newTournament.shuttle_type === 'nylon' ? 'border-yellow-500 bg-yellow-500/10 text-yellow-700 dark:text-yellow-300' : 'border-border bg-card hover:bg-muted/50'}`}
                      data-testid="shuttle-nylon"
                    >
                      🟡 Nylon
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewTournament({ ...newTournament, shuttle_type: 'feather' })}
                      className={`h-10 rounded-md border-2 text-sm font-medium transition-colors ${newTournament.shuttle_type === 'feather' ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'border-border bg-card hover:bg-muted/50'}`}
                      data-testid="shuttle-feather"
                    >
                      🪶 Feather
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start text-left font-normal',
                            !newTournament.start_date && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {newTournament.start_date ? format(newTournament.start_date, 'PPP') : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={newTournament.start_date}
                          onSelect={(date) => setNewTournament({ ...newTournament, start_date: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full justify-start text-left font-normal',
                            !newTournament.end_date && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {newTournament.end_date ? format(newTournament.end_date, 'PPP') : 'Pick date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={newTournament.end_date}
                          onSelect={(date) => setNewTournament({ ...newTournament, end_date: date })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateTournament}
                  disabled={creating}
                  className="bg-secondary hover:bg-secondary/90"
                  data-testid="submit-tournament-btn"
                >
                  {creating ? 'Creating...' : 'Create Tournament'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Tournaments Grid */}
        {tournaments.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No tournaments yet. Create one to get started!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tournaments.map((tournament) => (
              <Link key={tournament.tournament_id} to={`/tournaments/${tournament.tournament_id}`}>
                <Card className="hover:shadow-md hover:border-primary/50 transition-all h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <Badge className={getStatusBadge(tournament.status)}>
                        {tournament.status.replace('_', ' ')}
                      </Badge>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                    
                    <div className="flex items-center gap-3 mb-3">
                      <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center">
                        <Trophy className="h-6 w-6 text-accent" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-lg">{tournament.name}</h3>
                        <p className="text-sm text-muted-foreground capitalize">
                          {getFormatLabel(tournament.format)} • {tournament.match_type}
                        </p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mt-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span>
                          {tournament.teams?.length || 0} teams
                        </span>
                      </div>
                      {tournament.start_date && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CalendarIcon className="h-4 w-4" />
                          <span>{formatDate(tournament.start_date)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Progress bar for registration */}
                    {tournament.status === 'registration' && (
                      <div className="mt-4">
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary transition-all"
                            style={{
                              width: `${((tournament.participant_ids?.length || 0) / tournament.max_participants) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
